import {
  useCallback, useContext, useEffect, useMemo
} from "react";
import { ActionCreatorWithOptionalPayload } from "@reduxjs/toolkit";
import { INavLink, INavLinkGroup } from "@fluentui/react/lib/Nav";

import { hasNoElements } from "Utils/Array";
import { isCompany, isContact } from "Utils/IsType";
import { getName } from "Utils/Item";
import { getRelevantAddresses } from "Utils/Relevance";
import { IProject } from "Model/Storage/IProject";
import { IWithAddressAndName } from "Model/Storage/IWithAddressAndName";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAddressValidationActiveProjectItemId,
  selectAddressValidationCache,
  selectAddressValidationResults,
  selectDuplicateCheckActiveProjectItemId,
  selectDuplicateCheckCache,
  selectDuplicateCheckResults
} from "Store/Selectors/Omikron";
import {
  addressValidationActiveProjectIdSelect,
  addressValidationActiveProjectItemIdSelect,
  duplicateCheckActiveProjectIdSelect,
  duplicateCheckActiveProjectItemIdSelect
} from "Store/Actions";
import { IState } from "Model/State/IState";
import { IOmikronResult } from "Model/Omikron/IOmikronResult";
import { isNullOrWhiteSpace } from "Utils/String";
import { IAddressValidationResult } from "Model/Omikron/IAddressValidationResult";
import { IItem } from "Model/Storage/IItem";
import { DialogProjectItemIcon } from "../IDialogProps";
import { IUseNavigationItems } from "./IUseNavigationItems";
import { DialogType } from "../DialogType";
import { ProjectItemContext } from "../../ProjectItemContext/ProjectItemContext";

export function useNavigationItems(project: IProject): IUseNavigationItems {
  const dispatch = useDispatch();
  const { type } = useContext(ProjectItemContext);
  const cachedProject = useSelector(getCacheByType(type));
  const cachedRelevantItems = useMemo(() => getRelevantAddresses(cachedProject ?? project), [cachedProject, project]);
  const relevantItems = useMemo(() => getRelevantAddresses(project), [project]);
  const results = useSelector(getResultsByType(type));

  useEffect(() => {
    if (project == null) {
      return;
    }

    dispatch(getDispatchActiveProjectIdByType(type)(project[`@id`]));
    dispatch(getDispatchActiveProjectItemIdByType(type)(relevantItems[0][`@id`]));
  }, [dispatch, project, relevantItems, type]);

  const onSelect = useCallback(
    (itemId: string) => {
      dispatch(getDispatchActiveProjectItemIdByType(type)(itemId));
    },
    [dispatch, type]
  );

  const selectedNavigationItemId = useSelector(getSelectedProjectItemIdByType(type));

  const navigationItems: INavLinkGroup[] = useMemo(() => {
    if (hasNoElements(relevantItems)) {
      return [];
    }

    return [
      {
        links: relevantItems.map<INavLink>((item) => ({
          key: item[`@id`],
          name: getName(item),
          icon: getProjectItemIcon(item),
          url: null,
          forceAnchor: true,
          checked: getCheckedByType(type, item, cachedRelevantItems, results),
          onClick() {
            onSelect(item[`@id`]);
          }
        }))
      }
    ];
  }, [cachedRelevantItems, onSelect, relevantItems, results, type]);

  return {
    navigationItems,
    selectedNavigationItemId
  };
}

function getCacheByType(type: DialogType): (state: IState) => IProject {
  return type === DialogType.AddressValidation ? selectAddressValidationCache : selectDuplicateCheckCache;
}

function getResultsByType(type: DialogType): (state: IState) => IOmikronResult[][] {
  return type === DialogType.AddressValidation ? selectAddressValidationResults : selectDuplicateCheckResults;
}

export enum CheckedState {
  Checked,
  Merged,
  NoResult,
  Unchecked
}

function getCheckedByType(
  type: DialogType,
  item: IItem,
  cachedRelevantItems: IWithAddressAndName[],
  results: IOmikronResult[][]
): CheckedState {
  const id = item[`@id`];
  const result = results.find((resultEntries) => {
    if (hasNoElements(resultEntries) || isNullOrWhiteSpace(id)) {
      return false;
    }

    return resultEntries[0][`@id`] === id;
  });

  const isAVNotFound = getIsAVNotFound(type, result);
  if (hasNoElements(result) || isAVNotFound) {
    return CheckedState.NoResult;
  }

  const cachedItem = cachedRelevantItems.find((i) => i[`@id`] === id);
  const isMerged = cachedItem?.[`@targetId`] !== item?.[`@targetId`];
  if (isMerged) {
    return CheckedState.Merged;
  }

  const flagName = type === DialogType.AddressValidation ? `@isAV` : `@isDC`;
  const hasFlag = cachedItem?.[flagName] != null;
  if (!hasFlag) {
    return CheckedState.Unchecked;
  }

  if (cachedItem[flagName] === false) {
    return CheckedState.Unchecked;
  }

  return CheckedState.Checked;
}

function getIsAVNotFound(type: DialogType, results: IOmikronResult[]) {
  if (type !== DialogType.AddressValidation) {
    return false;
  }

  return results?.every((r) => (r as IAddressValidationResult).pkStatus === `NOF`) ?? false;
}

function getSelectedProjectItemIdByType(type: DialogType): (state: IState) => string {
  return type === DialogType.AddressValidation
    ? selectAddressValidationActiveProjectItemId
    : selectDuplicateCheckActiveProjectItemId;
}

function getDispatchActiveProjectIdByType(type: DialogType): ActionCreatorWithOptionalPayload<string, string> {
  return type === DialogType.AddressValidation
    ? addressValidationActiveProjectIdSelect
    : duplicateCheckActiveProjectIdSelect;
}

function getDispatchActiveProjectItemIdByType(type: DialogType): ActionCreatorWithOptionalPayload<string, string> {
  return type === DialogType.AddressValidation
    ? addressValidationActiveProjectItemIdSelect
    : duplicateCheckActiveProjectItemIdSelect;
}

function getProjectItemIcon(item: IWithAddressAndName): DialogProjectItemIcon {
  if (isContact(item)) {
    return `Contact`;
  }

  if (isCompany(item)) {
    return `CityNext`;
  }

  return `Manufacturing`;
}
